import { Alert, Box, Button, Checkbox, FormControlLabel, Grid, Typography } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import FormLoaderWrapper from 'src/shared/components/organisms/formLoaderWrapper';
import { DeleteConfirmationDialog } from 'src/shared/components/organisms/deleteConfirmationDialog';
import { useUnsavedChangesWarning } from 'src/shared/hooks/useUnsavedChangesWarning';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { FormProvider, useForm } from 'react-hook-form';
import { useGlobals } from 'src/shared/contexts/appContext';
import { toast } from 'react-toastify';
import { getAssociationFeeById, getAssociationFeePreview, previewAssociationFee, updateAssociationFee } from 'src/shared/services/associationService';
import { FeePreview } from 'src/modules/district/models/districtFee';
import { getOrganizationFee } from 'src/shared/services/organizationService';
import { getOrganizationAssociationId } from 'src/shared/functions/organizationHelpers';
import { AutocompleteDropdown } from 'src/shared/components/form/autocompleteDropdown';
import { getPaymentMethodOptions, PaymentMethodEnum } from 'src/shared/models/enums/paymentMethod.enum';
import { MainSettingsEnum } from 'src/shared/models/enums/mainSettings.enum';
import { getMainSetting } from 'src/shared/functions/getMainSetting';
import { Add } from '@mui/icons-material';
import { AssociationFeeTable } from '../components/associationFeeTable';
import { FeePeriodSection } from '../components/feePeriodSection';
import { FeeLevelEnum } from '../models/feePeriod';
import { AssociationFeeRequestSchema } from '../schemas/associationFeeRequestSchema';
import { AssociationFeeRequest } from '../models/associationFeeRequest';
import { AssociationFee, PaymentMethodOption } from '../models/associationFee';

export const AssociationFeePage = () => {
  const { t } = useTranslation();
  const { currentUser } = useGlobals();
  const params = useParams();
  const isMounted = useIsMounted();

  // Avgiften får bara ändras av den som har behörighet enligt inställningen i medlemsregistret.
  // API:et avgör, sidan speglar bara svaret.
  const [canEditFee, setCanEditFee] = useState<boolean>(true);
  const readOnlyMode = !canEditFee;

  const currentAssocationId = getOrganizationAssociationId(currentUser, params.id);
  const paymentMethodOptions = getPaymentMethodOptions(t);

  // Vänmedlem är ett eget belopp vid sidan av aviseringsmetodens rutor och gäller alla metoder,
  // men bara för kunder som har modulinställningen påslagen.
  const useNewFriend = getMainSetting(currentUser?.mainSettings ?? [], MainSettingsEnum.UseNewFriend, 'False') === 'True';

  const [association, setAssociation] = useState<AssociationFee>();
  const [loading, setLoading] = useState<boolean>(false);
  const [openAddprofile, setOpenAddprofile] = useState<boolean>(false);
  const [feePreview, setFeePreview] = useState<FeePreview>();

  const schema = AssociationFeeRequestSchema(t);
  const formMethods = useForm<AssociationFeeRequest>({
    resolver: yupResolver(schema),
    mode: 'all',
    defaultValues: {
      localAss_id: Number(currentAssocationId) || 0,
      paymentMethod: 0,
      ocr: 'false',
      sameFeeForAll: 0,
      mainAndFamily: {
        feeMainMember: 0,
        feeFamilyMember: 0,
        feeMainMemberAutogiro: 0,
        feeFamilyMemberAutogiro: 0,
        feeMainMemberSwish: 0,
        feeFamilyMemberSwish: 0,
      },
      familySteps: [
        { member: 'mainMember', fee: 0 },
        { member: 'familyMember1', fee: 0 },
      ],
      oneFeeForHousehold: 0,
      feeBasedOnProfile: undefined,
    },
  });

  const {
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isDirty, isSubmitting },
  } = formMethods;

  const blocker = useUnsavedChangesWarning(isDirty && !isSubmitting);

  // Summeringen ska följa med medan man skriver. Värdena skickas till API:t, som räknar med
  // samma regler som fakturan, i stället för att gränssnittet försöker lägga ihop nivåerna
  // själv. Ett profilbelopp som är 0 nollställer nivåns avgift, och den sortens regel vill vi
  // bara ha på ett ställe.
  const watchedValues = watch();

  useEffect(() => {
    if (!association?.distributedFee || !currentAssocationId) return undefined;

    const timer = setTimeout(async () => {
      try {
        const preview = await previewAssociationFee(currentAssocationId, formMethods.getValues());

        if (isMounted()) setFeePreview(preview.data);
      } catch {
        // Summeringen är extra information, sidan fungerar utan den.
      }
    }, 400);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(watchedValues), association?.distributedFee, currentAssocationId]);
  ConsoleHelper.log('Form validation errors:', errors);

  const paymentMethod = watch('paymentMethod');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const feeResult = await getAssociationFeeById(currentAssocationId);

      if (isMounted()) {
        setAssociation(feeResult?.data);
        setCanEditFee(feeResult?.data?.canEdit !== false);

        // Summeringen visar vad medlemmen faktiskt betalar, hämtad från API:t så att
        // reglerna inte dupliceras i gränssnittet.
        if (feeResult.data.distributedFee) {
          try {
            const preview = await getAssociationFeePreview(currentAssocationId);
            if (isMounted()) setFeePreview(preview.data);
          } catch {
            // Summeringen är extra information, sidan fungerar utan den.
          }
        }

        // Med fördelningsbaserad avgift är föreningens belopp dess egen del, och ska alltså
        // visas och gå att ändra. Utan den gäller förbundets avgift som tidigare.
        let feeData = feeResult.data;
        if (feeResult.data.centralConfig && !feeResult.data.distributedFee) {
          try {
            const orgFeeResult = await getOrganizationFee();
            feeData = orgFeeResult.data;
          } catch {
            // Fallback till föreningens egna data
          }
        }

        // Ladda formuläret med reset() i stället för enskilda setValue-anrop. setValue synkar
        // inte useFieldArray (feeBasedOnProfile), så profilraderna visade gamla belopp efter
        // spara tills sidan laddades om. reset() återinitierar både värden och field-arrayen.
        reset({
          localAss_id: Number(currentAssocationId) || 0,
          paymentMethod: feeData.paymentMethod ?? 0,
          ocr: feeData.ocr?.toString() ?? 'false',
          sameFeeForAll: feeData.sameFeeForAll,
          sameFeeForAllAutogiro: feeData.sameFeeForAllAutogiro ?? undefined,
          sameFeeForAllSwish: feeData.sameFeeForAllSwish ?? undefined,
          mainAndFamily: feeData.mainAndFamily ?? {
            feeMainMember: 0,
            feeFamilyMember: 0,
            feeMainMemberAutogiro: 0,
            feeFamilyMemberAutogiro: 0,
            feeMainMemberSwish: 0,
            feeFamilyMemberSwish: 0,
          },
          familySteps: feeData.familySteps ?? [
            { member: 'mainMember', fee: 0 },
            { member: 'familyMember1', fee: 0 },
          ],
          oneFeeForHousehold: feeData.oneFeeForHousehold,
          oneFeeForHouseholdAutogiro: feeData.oneFeeForHouseholdAutogiro ?? undefined,
          oneFeeForHouseholdSwish: feeData.oneFeeForHouseholdSwish ?? undefined,
          feeBasedOnProfile: feeData.feeBasedOnProfile,
          feeFriend: feeData.feeFriend ?? undefined,
          feeFriendAutogiro: feeData.feeFriendAutogiro ?? undefined,
          feeFriendSwish: feeData.feeFriendSwish ?? undefined,
        });
      }
    } catch (e) {
      if (isMounted()) ConsoleHelper.log(e);
    } finally {
      if (isMounted()) setLoading(false);
    }
  }, [currentAssocationId, isMounted, reset]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const selectedPaymentMethod = paymentMethodOptions.find((opt) => opt.value === watch('paymentMethod'));

  const FieldsToClean: Record<PaymentMethodEnum, Array<keyof AssociationFeeRequest>> = {
    [PaymentMethodEnum.SameFeeForAll]: ['mainAndFamily', 'familySteps', 'oneFeeForHousehold'],
    [PaymentMethodEnum.MainAndFamily]: ['sameFeeForAll', 'familySteps', 'oneFeeForHousehold'],
    [PaymentMethodEnum.FamilySteps]: ['sameFeeForAll', 'mainAndFamily', 'oneFeeForHousehold'],
    [PaymentMethodEnum.OneFeeForHousehold]: ['sameFeeForAll', 'mainAndFamily', 'familySteps'],
    [PaymentMethodEnum.FeeBasedOnProfile]: ['sameFeeForAll', 'mainAndFamily', 'familySteps', 'oneFeeForHousehold'],
    [PaymentMethodEnum.None]: ['sameFeeForAll', 'mainAndFamily', 'familySteps', 'oneFeeForHousehold'],
  };

  const onSubmit = async (data: AssociationFeeRequest) => {
    setLoading(true);

    const cleanedData: AssociationFeeRequest = {
      ...data,
      ocr: data.ocr === 'true',
    };
    if (typeof paymentMethod !== 'number') return;
    FieldsToClean[paymentMethod as PaymentMethodEnum]?.forEach((field) => {
      (cleanedData as Partial<AssociationFeeRequest>)[field] = undefined;
    });

    try {
      await updateAssociationFee(cleanedData);
      if (isMounted()) {
        toast.success(<>{t('association:feeUpdateSuccess')}</>);
        fetchData();
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      if (isMounted()) {
        const errorMessage = e?.data?.messages;

        // Interceptorn visar redan serverns meddelande. Utan den här kontrollen kom en tom röd
        // toast ovanpå den när felet är en UserException, som bara bär title.
        if (errorMessage) toast.error(<>{errorMessage}</>);
      }
    } finally {
      if (isMounted()) setLoading(false);
    }
  };

  return (
    <FormLoaderWrapper loading={loading}>
      <FormProvider {...formMethods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DeleteConfirmationDialog
            open={blocker.state === 'blocked'}
            title={t('association:unsavedChangesTitle')}
            modaltext={t('association:unsavedChangesText')}
            value='leave'
            confirmButtonText={t('association:unsavedChangesLeave')}
            onClose={(result) => {
              if (result?.id === 'leave') {
                blocker.proceed?.();
                return;
              }

              blocker.reset?.();
            }}
          />

          <Box sx={{ p: 2 }}>
            <Grid container>
              {association?.centralConfig && !association?.distributedFee && (
                <Grid item xs={12} mb={3}>
                  <Alert severity='warning'>
                    {t('association:centralNotice')}. {t('association:centralNoticeOnlyFederation')}
                  </Alert>
                </Grid>
              )}
              {!canEditFee && (
                <Grid item xs={12}>
                  <Alert severity='info'>{t('common:feeNoPermission')}</Alert>
                </Grid>
              )}

              {association?.distributedFee && (
                <Grid item xs={12} mb={3}>
                  <Alert severity='info'>{t('association:distributedFeeAssociationInfo')}</Alert>
                </Grid>
              )}
              {/* I fördelningsläget är beloppen föreningens egen del och ska gå att ändra, trots
                  att den centrala inkasseringen är påslagen. */}
              <Grid item xs={12}>
                <FeePeriodSection
                  level={FeeLevelEnum.LocalAss}
                  associationId={Number(currentAssocationId) || 0}
                  autogiroFee={currentUser?.autogiroEnabled}
                  swishFee={currentUser?.swishRecurringEnabled}
                  amountHeader={association?.distributedFee ? t('association:feeTableLocalPart') : t('association:feeTableAmount')}
                  showFriendFee={useNewFriend}
                  distributedFee={association?.distributedFee === true}
                  lockedProfiles={association?.distributedFee === true}
                  readOnlyMode={readOnlyMode || (association?.centralConfig === true && !association?.distributedFee)}
                  header={({ hasPlannedPeriods }) => (
                    <Grid
                      container
                      spacing={2}
                      alignItems='center'
                      mb={2}
                      sx={association?.centralConfig && !association?.distributedFee ? { opacity: 0.5, pointerEvents: 'none' } : {}}
                    >
                      <Grid item xs={12} md={4}>
                        {/* Vid fördelningsbaserad avgift är det förbundet som väljer vilka
                            föreningar som ingår i den centrala inkasseringen, så föreningen kan
                            inte kryssa ur sig själv här. */}
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={watch('ocr') === 'true' || watch('ocr') === true}
                              onChange={(e) => setValue('ocr', e.target.checked ? 'true' : 'false', { shouldDirty: true })}
                              disabled={association?.centralConfig === true || association?.distributedFee === true}
                            />
                          }
                          label={
                            association?.distributedFee
                              ? `${t('association:useOcr')} (${t('association:distributedFeeMethodLocked')})`
                              : t('association:useOcr')
                          }
                        />
                      </Grid>
                      <Grid item xs={12} md={5}>
                        {/* Aviseringsmetoden är gemensam för alla tre nivåerna i fördelningsläget och
                        styrs av förbundet, därför visas den låst här. */}
                        <AutocompleteDropdown<PaymentMethodOption>
                          options={paymentMethodOptions}
                          disable={readOnlyMode || association?.distributedFee === true || hasPlannedPeriods}
                          name='paymentMethod'
                          nameProp='description'
                          placeholder={t('association:paymentMethod.none')}
                          emptyLabel={t('association:paymentMethod.none')}
                          value={selectedPaymentMethod}
                          keyProp='id'
                          label={
                            // eslint-disable-next-line no-nested-ternary
                            association?.distributedFee
                              ? `Aviseringsmetod (${t('association:distributedFeeMethodLocked')})`
                              : hasPlannedPeriods
                              ? `Aviseringsmetod (${t('association:feePeriodMethodLocked')})`
                              : 'Aviseringsmetod'
                          }
                          callback={(val) => {
                            setValue('paymentMethod', val?.value);
                          }}
                        />
                      </Grid>
                    </Grid>
                  )}
                >
                  {({ hasPlannedPeriods }) => (
                    <Grid item xs={12} sx={association?.centralConfig && !association?.distributedFee ? { opacity: 0.5, pointerEvents: 'none' } : {}}>
                      <Grid container>
                        <Grid item xs={12}>
                          <AssociationFeeTable
                            autogiroFee={currentUser?.autogiroEnabled}
                            swishFee={currentUser?.swishRecurringEnabled}
                            readOnlyMode={readOnlyMode || hasPlannedPeriods}
                            paymentMethod={paymentMethod || 0}
                            showFriendFee={useNewFriend}
                            distributedFee={association?.distributedFee === true}
                            lockedProfiles={association?.distributedFee === true}
                            amountHeader={association?.distributedFee ? t('association:feeTableLocalPart') : t('association:feeTableAmount')}
                            feePreview={feePreview?.distributedFee ? feePreview : undefined}
                            openAddprofile={openAddprofile}
                            setOpenAddprofile={setOpenAddprofile}
                          />
                        </Grid>
                      </Grid>
                      {!readOnlyMode && !hasPlannedPeriods && (
                        <Grid item xs={12} sx={{ display: 'flex', justifyContent: { xs: 'space-between', sm: 'flex-end' }, mt: 2 }}>
                          {/* Profilerna styrs av förbundet i fördelningsläget och läggs till där. */}
                          {!association?.distributedFee && (
                            <Button
                              variant='outlined'
                              size='small'
                              disableElevation
                              onClick={() => {
                                setOpenAddprofile(true);
                              }}
                            >
                              <Add fontSize='small' />
                              {t('association:addProfile')}
                            </Button>
                          )}

                          <Button
                            disabled={paymentMethod === undefined || paymentMethod === 0}
                            sx={{ marginLeft: 2 }}
                            type='submit'
                            variant='contained'
                            disableElevation
                          >
                            {t('common:button.save')}
                          </Button>
                        </Grid>
                      )}
                    </Grid>
                  )}
                </FeePeriodSection>
              </Grid>
            </Grid>
          </Box>
        </form>
      </FormProvider>
    </FormLoaderWrapper>
  );
};
